// Barsys AI app (com.barsys.barsysai): iOS vs Android, from BigQuery.
// Backed by GET /secureApi/analytics/barsys-ai/overview (Analytics-Dashboard-API).
//
// The Firebase Analytics pages mix all 8 app streams in GA4 property 164367698
// (mostly the legacy app). This page is AI-app only and puts the two
// platforms side by side.

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Chart from "react-apexcharts";
import { Card, CardHeader, CardBody } from "../../components/card/card.jsx";

const DAY_OPTIONS = [7, 14, 30, 60, 90];

const PLATFORM_LABEL = { IOS: "iOS", ANDROID: "Android", ALL: "All" };

const TREND_METRICS = [
  { key: "active_users", label: "Active users" },
  { key: "new_users", label: "New users" },
  { key: "sessions", label: "Sessions" },
  { key: "craft_sessions", label: "Sessions with a completed craft" },
];

const FEATURES = [
  { key: "bluetooth", label: "Bluetooth / pairing flow" },
  { key: "viewed_recipe", label: "Viewed a recipe" },
  { key: "mybar", label: "My Bar" },
  { key: "barbot", label: "Barbot (AI)" },
  { key: "craft", label: "Started a craft" },
  { key: "craft_completed", label: "Completed a craft" },
  { key: "explore", label: "Explore" },
  { key: "favorited", label: "Favourited a recipe" },
  { key: "device_paired", label: "Machine paired" },
];

function cssVar(name) {
  return getComputedStyle(document.body).getPropertyValue(name).trim();
}

const fmtInt = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString());
const fmtPct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
const fmtDuration = (sec) => {
  if (sec === null || sec === undefined) return "—";
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return m ? `${m}m ${s}s` : `${s}s`;
};

// The API enforces users:access; this only decides whether to render the section.
function canSeeContacts() {
  try {
    if (Number(localStorage.getItem("adminFlag")) === 1) return true;
    return JSON.parse(localStorage.getItem("permissions") || "[]").includes("users:access");
  } catch {
    return false;
  }
}

const fmtDate = (v) => (v ? String(v).slice(0, 10) : "—");

// Signed-in app users with contact details (name, email, phone) from Ory.
// Personal data: served by GET /barsys-ai/users, which requires users:access
// server-side. Buyers' order addresses are deliberately not linked here.
function SignedInUsers({ days }) {
  const PAGE_SIZE = 50;
  const [platform, setPlatform] = useState("ALL");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [days, platform]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${process.env.REACT_APP_ADMIN_API_ADDRESS}/secureApi/analytics/barsys-ai/users`, {
        params: { days, platform, search: search || undefined, page, pageSize: PAGE_SIZE },
        headers: { Authorization: `Bearer ${localStorage.getItem("jwtToken")}` },
      })
      .then((resp) => {
        if (!cancelled) setResult(resp.data.data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.status === 403 ? "You need the Users permission to see contact details." : err.response?.data?.message || err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days, platform, search, page]);

  const pages = result ? Math.max(1, Math.ceil(result.total / PAGE_SIZE)) : 1;

  return (
    <Card className="mb-3">
      <CardHeader className="d-flex align-items-center flex-wrap fw-bold small">
        SIGNED-IN USERS
        <span className="fw-normal text-inverse text-opacity-50 ms-2">
          · active in the last {days} days{result ? ` · ${result.total.toLocaleString()} accounts` : ""}
        </span>
        <div className="ms-auto d-flex gap-2 mt-2 mt-md-0">
          <input
            type="search"
            className="form-control form-control-sm"
            style={{ width: 240 }}
            placeholder="Search name, email or phone"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <select className="form-select form-select-sm w-auto" value={platform} onChange={(e) => setPlatform(e.target.value)}>
            <option value="ALL">All platforms</option>
            <option value="IOS">iOS</option>
            <option value="ANDROID">Android</option>
          </select>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        {error && <div className="alert alert-danger m-3">{error}</div>}
        {!error && (
          <div className="table-responsive" style={{ opacity: loading ? 0.5 : 1 }}>
            <table className="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th className="ps-3">First name</th>
                  <th>Last name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Platform</th>
                  <th>App version</th>
                  <th>Country</th>
                  <th className="text-end">Sessions</th>
                  <th className="text-end">Crafting sessions</th>
                  <th>Last seen</th>
                  <th className="pe-3">Signed up</th>
                </tr>
              </thead>
              <tbody>
                {(result?.rows || []).map((u) => (
                  <tr key={u.account_id}>
                    <td className="ps-3"><Link to={`/users/activity?type=ai&id=${encodeURIComponent(u.account_id)}`} title="View this user's drinks, machines and reviews">{u.first_name || "(no name)"}</Link></td>
                    <td>{u.last_name || "—"}</td>
                    <td>{u.email ? <a href={`mailto:${u.email}`}>{u.email}</a> : "—"}</td>
                    <td className="text-nowrap">{u.phone || "—"}</td>
                    <td>{u.platforms.map((p) => PLATFORM_LABEL[p] || p).join(", ")}</td>
                    <td>{u.app_version || "—"}</td>
                    <td>{u.country || "—"}</td>
                    <td className="text-end">{fmtInt(u.sessions)}</td>
                    <td className="text-end">{fmtInt(u.craft_sessions)}</td>
                    <td className="text-nowrap">{fmtDate(u.last_seen)}</td>
                    <td className="text-nowrap pe-3">{u.in_ory ? fmtDate(u.signed_up_at) : <span className="text-inverse text-opacity-50">not in Ory</span>}</td>
                  </tr>
                ))}
                {result && result.rows.length === 0 && (
                  <tr>
                    <td colSpan={11} className="text-center text-inverse text-opacity-50 py-3">No matching users</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {result && !error && (
          <div className="d-flex align-items-center flex-wrap small px-3 py-2">
            <span className="text-inverse text-opacity-50">
              This page: {result.coverage.with_name}/{result.coverage.of} with a name, {result.coverage.with_email}/{result.coverage.of} with an email,{" "}
              {result.coverage.with_phone}/{result.coverage.of} with a phone. Contact details come from Ory (the app's sign-in system). There is no postal address for app users.
            </span>
            <div className="ms-auto btn-group">
              <button className="btn btn-sm btn-outline-theme" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>‹</button>
              <span className="btn btn-sm btn-outline-theme disabled">
                {page} / {pages}
              </span>
              <button className="btn btn-sm btn-outline-theme" disabled={page >= pages || loading} onClick={() => setPage((p) => p + 1)}>›</button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function BarsysAiApp() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trendMetric, setTrendMetric] = useState("active_users");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${process.env.REACT_APP_ADMIN_API_ADDRESS}/secureApi/analytics/barsys-ai/overview`, {
        params: { days },
        headers: { Authorization: `Bearer ${localStorage.getItem("jwtToken")}` },
      })
      .then((resp) => {
        if (!cancelled) setData(resp.data.data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.message || err.message || "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  // Fixed per-platform colours: the theme colour is user-selectable (and can be
  // near-black), so it cannot be relied on to tell the two platforms apart.
  const colors = { IOS: "#3cd2a5", ANDROID: "#30beff" };

  const inverse = cssVar("--bs-inverse") || "#fff";
  const font = cssVar("--bs-body-font-family");

  const gapFor = (platform, field) =>
    (data?.tracking_gaps || []).find((g) => g.platform === platform && g.field === field);

  // ---------- KPI comparison table ----------
  const kpiRows = useMemo(() => {
    if (!data) return [];
    const k = data.kpis || {};
    const r = data.retention || {};
    const s = data.stability || {};
    const row = (label, get, fmt, hint) => ({
      label,
      hint,
      values: ["IOS", "ANDROID", "ALL"].map((p) => fmt(get(p))),
    });
    return [
      row("Daily active users", (p) => k[p]?.dau, fmtInt, `Last complete day (${data.as_of})`),
      row("Weekly active users", (p) => k[p]?.wau, fmtInt, "7 days"),
      row("Monthly active users", (p) => k[p]?.mau, fmtInt, "30 days"),
      row("Stickiness (DAU / MAU)", (p) => k[p]?.stickiness_pct, fmtPct),
      row("Active users", (p) => k[p]?.active_users, fmtInt, `Last ${data.days} days`),
      row("New users", (p) => k[p]?.new_users, fmtInt, `First session in the last ${data.days} days`),
      row("Sessions", (p) => k[p]?.sessions, fmtInt),
      row("Sessions per user", (p) => k[p]?.sessions_per_user, (v) => (v ?? "—")),
      row("Signed in", (p) => k[p]?.signed_in_pct, fmtPct, "Share of active users with an account"),
      row("Median session", (p) => k[p]?.median_session_sec, fmtDuration),
      row("Engaged sessions", (p) => k[p]?.engaged_session_pct, fmtPct),
      row("Day-1 retention", (p) => r[p]?.day1_pct, fmtPct, "Users who first opened the app 15–75 days ago"),
      row("Week-2 retention", (p) => r[p]?.week2_pct, fmtPct, "Returned on days 7–13"),
      row("Crash-free sessions", (p) => (p === "ALL" ? null : s[p]?.crash_free_sessions_pct), fmtPct, "Crashlytics"),
      row("Crashes per 1k sessions", (p) => (p === "ALL" ? null : s[p]?.crashes_per_1k_sessions), (v) => (v ?? "—")),
    ];
  }, [data]);

  // ---------- Trend chart ----------
  const trendChart = useMemo(() => {
    if (!data) return null;
    const dates = [...new Set(data.trend.map((t) => t.date))].sort();
    const series = ["IOS", "ANDROID"].map((p) => {
      const byDate = new Map(data.trend.filter((t) => t.platform === p).map((t) => [t.date, t[trendMetric]]));
      return { name: PLATFORM_LABEL[p], data: dates.map((d) => byDate.get(d) ?? 0) };
    });
    return {
      series,
      options: {
        chart: { type: "line", toolbar: { show: false }, fontFamily: font, zoom: { enabled: false } },
        colors: [colors.IOS, colors.ANDROID],
        stroke: { width: 2, curve: "smooth" },
        xaxis: { categories: dates, labels: { style: { colors: inverse }, rotate: -45 }, tickAmount: Math.min(dates.length, 12) },
        yaxis: { labels: { style: { colors: inverse } }, min: 0 },
        legend: { labels: { colors: inverse } },
        grid: { borderColor: `rgba(${cssVar("--bs-inverse-rgb") || "255,255,255"}, .15)` },
        tooltip: { theme: "dark" },
        dataLabels: { enabled: false },
      },
    };
  }, [data, trendMetric, colors, inverse, font]);

  // ---------- Feature adoption chart ----------
  const featureChart = useMemo(() => {
    if (!data) return null;
    const f = data.features || {};
    return {
      series: ["IOS", "ANDROID"].map((p) => ({
        name: PLATFORM_LABEL[p],
        data: FEATURES.map(({ key }) => (gapFor(p, key) ? null : f[p]?.[key]?.pct ?? 0)),
      })),
      options: {
        chart: { type: "bar", toolbar: { show: false }, fontFamily: font },
        colors: [colors.IOS, colors.ANDROID],
        plotOptions: { bar: { horizontal: true, barHeight: "70%" } },
        dataLabels: { enabled: false },
        xaxis: { categories: FEATURES.map((x) => x.label), max: 100, labels: { style: { colors: inverse }, formatter: (v) => `${v}%` } },
        yaxis: { labels: { style: { colors: inverse } } },
        legend: { labels: { colors: inverse } },
        grid: { borderColor: `rgba(${cssVar("--bs-inverse-rgb") || "255,255,255"}, .15)` },
        tooltip: { theme: "dark", y: { formatter: (v) => (v === null ? "not tracked" : `${v}% of active users`) } },
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, colors, inverse, font]);

  const PlatformTable = ({ title, rows, columns, empty = "No data" }) => (
    <div className="col-md-6 mb-3">
      <div className="fw-bold small text-inverse text-opacity-75 mb-2">{title}</div>
      {rows && rows.length ? (
        <table className="table table-sm mb-0">
          <thead>
            <tr>{columns.map((c) => <th key={c.key} className={c.num ? "text-end" : ""}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {columns.map((c) => (
                  <td key={c.key} className={c.num ? "text-end" : "text-truncate"} style={c.num ? undefined : { maxWidth: 320 }} title={c.num ? undefined : String(r[c.key] ?? "")}>
                    {c.num ? fmtInt(r[c.key]) : r[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="small text-inverse text-opacity-50">{empty}</div>
      )}
    </div>
  );

  const byPlatformSection = (title, key, columns, empty) => (
    <Card className="mb-3">
      <CardHeader className="fw-bold small">{title}</CardHeader>
      <CardBody>
        <div className="row">
          {["IOS", "ANDROID"].map((p) => (
            <PlatformTable key={p} title={PLATFORM_LABEL[p]} rows={key(p)} columns={columns} empty={empty} />
          ))}
        </div>
      </CardBody>
    </Card>
  );

  return (
    <div>
      <div className="d-flex align-items-center flex-wrap mb-3">
        <div>
          <ul className="breadcrumb">
            <li className="breadcrumb-item">FIREBASE ANALYTICS</li>
            <li className="breadcrumb-item active">BARSYS AI APP</li>
          </ul>
          <h1 className="page-header mb-0">
            Barsys AI App <small>iOS vs Android</small>
          </h1>
          <div className="small text-inverse text-opacity-50 mt-1">
            Source: BigQuery{data?.as_of ? ` · data through ${data.as_of}` : ""} · com.barsys.barsysai only
          </div>
        </div>
        <div className="ms-auto btn-group mt-2 mt-md-0">
          {DAY_OPTIONS.map((d) => (
            <button key={d} type="button" className={`btn btn-sm ${d === days ? "btn-theme" : "btn-outline-theme"}`} onClick={() => setDays(d)} disabled={loading}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading && !data && (
        <div className="text-center py-5">
          <div className="spinner-border text-theme" />
        </div>
      )}

      {data && (
        <div style={{ opacity: loading ? 0.5 : 1, transition: "opacity .2s" }}>
          {(data.tracking_gaps || []).map((g) => (
            <div key={`${g.platform}-${g.field}`} className="alert alert-warning py-2 small">
              <i className="fa fa-exclamation-triangle me-2" />
              Tracking gap: {g.note}
            </div>
          ))}

          <div className="row">
            <div className="col-xl-5">
              <Card className="mb-3">
                <CardHeader className="fw-bold small">KEY METRICS</CardHeader>
                <CardBody className="p-0">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th className="ps-3">Metric</th>
                        <th className="text-end" style={{ color: colors.IOS }}>iOS</th>
                        <th className="text-end" style={{ color: colors.ANDROID }}>Android</th>
                        <th className="text-end pe-3">All</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kpiRows.map((r) => (
                        <tr key={r.label}>
                          <td className="ps-3" title={r.hint}>
                            {r.label}
                            {r.hint && <div className="small text-inverse text-opacity-50">{r.hint}</div>}
                          </td>
                          {r.values.map((v, i) => (
                            <td key={i} className={`text-end ${i === 2 ? "pe-3" : ""}`}>{v}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardBody>
              </Card>
            </div>

            <div className="col-xl-7">
              <Card className="mb-3">
                <CardHeader className="d-flex align-items-center fw-bold small">
                  DAILY TREND
                  <select className="form-select form-select-sm ms-auto w-auto" value={trendMetric} onChange={(e) => setTrendMetric(e.target.value)}>
                    {TREND_METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                  </select>
                </CardHeader>
                <CardBody>
                  {trendChart && <Chart type="line" height={300} series={trendChart.series} options={trendChart.options} />}
                </CardBody>
              </Card>

              <Card className="mb-3">
                <CardHeader className="fw-bold small">FEATURE ADOPTION <span className="fw-normal text-inverse text-opacity-50">· share of active users, last {data.days} days</span></CardHeader>
                <CardBody>
                  {featureChart && <Chart type="bar" height={380} series={featureChart.series} options={featureChart.options} />}
                </CardBody>
              </Card>
            </div>
          </div>

          {byPlatformSection(
            `CRAFTS BY MACHINE · backend-confirmed pours, last ${data.days} days`,
            (p) => data.crafts_by_machine?.[p],
            [
              { key: "machine", label: "Machine" },
              { key: "crafts", label: "Crafts", num: true },
              { key: "crafting_users", label: "Users crafting", num: true },
              { key: "connected_machines", label: "Machines connected", num: true },
              { key: "connects", label: "Connections", num: true },
            ]
          )}

          {byPlatformSection(
            `TOP CRASHES · Crashlytics, last ${data.days} days`,
            (p) => data.stability?.[p]?.top_issues,
            [
              { key: "title", label: "Issue" },
              { key: "events", label: "Crashes", num: true },
              { key: "installs", label: "Installs hit", num: true },
              { key: "latest_version", label: "Latest version" },
            ],
            "No crashes in this window"
          )}

          <div className="row">
            <div className="col-xl-6">
              {byPlatformSection(
                "APP VERSIONS · active users",
                (p) => data.versions?.[p],
                [
                  { key: "version", label: "Version" },
                  { key: "users", label: "Users", num: true },
                  { key: "sessions", label: "Sessions", num: true },
                ]
              )}
            </div>
            <div className="col-xl-6">
              {byPlatformSection(
                "COUNTRIES · active users",
                (p) => data.countries?.[p],
                [
                  { key: "country", label: "Country" },
                  { key: "users", label: "Users", num: true },
                ]
              )}
            </div>
          </div>

          {canSeeContacts() && <SignedInUsers days={data.days} />}

          <div className="small text-inverse text-opacity-50 mb-4">
            Sessions and features: GA4 export mart (rebuilt daily 13:30 UTC). Stability: Firebase Crashlytics. Crafts: AI-app backend events (~15 min lag).
          </div>
        </div>
      )}
    </div>
  );
}

export default BarsysAiApp;
